import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useAcList, useAcMutations, type AcPoint } from "../api/accessControlApi";
import {
  ConsolePage,
  DetailRow,
  Drawer,
  FilterSelect,
  LoadState,
  METHOD_ICON,
  POINT_ICON,
  POINT_TONE,
  Panel,
  SITE_ICON,
  SearchBox,
  SegmentedToggle,
  SiteTree,
  StatusChip,
  ToolButton,
  relativeTime,
  toneOf,
  useTicker,
  type TreeGroup,
} from "../components/console/Console";
import FormModal, { type FormField, type FormValues } from "../components/console/FormModal";
import { EventRow } from "./AccessDashboard";

// ===========================================================================
// Control de acceso → Puertas y torniquetes: árbol de sitios, tarjetas o tabla
// de puntos de acceso con la cámara que los vigila, y ficha con sus últimos
// eventos. Alta, edición y baja por el API.
// ===========================================================================

const TYPES = ["door", "turnstile", "barrier", "gate", "elevator"] as const;
const STATUSES = ["online", "offline", "maintenance", "alarm"] as const;
const METHODS = ["card", "face", "qr", "plate", "pin", "fingerprint"] as const;

export default function AccessPointsConsole() {
  const { t, i18n } = useTranslation("accessConsole");
  const now = useTicker();
  const pointsQ = useAcList("points");
  const sitesQ = useAcList("sites");
  const camerasQ = useAcList("cameras");
  const { create, update, remove } = useAcMutations("points");

  const [site, setSite] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [view, setView] = useState<"grid" | "table">("grid");
  const [openId, setOpenId] = useState<string | null>(null);
  const [form, setForm] = useState<null | { mode: "create" | "edit"; point?: AcPoint }>(null);

  const points = useMemo(() => pointsQ.data ?? [], [pointsQ.data]);
  const camById = useMemo(() => new Map((camerasQ.data ?? []).map((c) => [c.id, c])), [camerasQ.data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return points.filter((p) => (!site || p.site_id === site) && (!type || p.type === type) && (!status || p.status === status) && (!q || `${p.name} ${p.zone} ${p.site_name}`.toLowerCase().includes(q)));
  }, [points, site, type, status, search]);
  const current = points.find((p) => p.id === openId) ?? null;
  const eventsQ = useAcList("events", current ? { point_id: current.id, limit: 8 } : { limit: 1 });

  const groups: TreeGroup[] = (sitesQ.data ?? []).map((s) => ({
    id: s.id,
    label: s.name,
    icon: SITE_ICON[s.type ?? "facility"] ?? SITE_ICON.facility,
    items: points.filter((p) => p.site_id === s.id).map((p) => ({ id: p.id, label: p.name, icon: POINT_ICON[p.type] ?? "ri-door-line", tone: toneOf(POINT_TONE, p.status) })),
  }));

  const fields: FormField[] = [
    { key: "name", label: t("fields.name"), required: true, full: true },
    { key: "site_id", label: t("fields.site"), type: "select", required: true, options: (sitesQ.data ?? []).map((s) => ({ value: s.id, label: s.name })) },
    { key: "zone", label: t("fields.zone") },
    { key: "type", label: t("fields.type"), type: "select", required: true, options: TYPES.map((v) => ({ value: v, label: t(`pointType.${v}`) })) },
    { key: "direction", label: t("fields.direction"), type: "select", required: true, options: ["entry", "exit", "bidirectional"].map((v) => ({ value: v, label: t(`direction.${v}`) })) },
    { key: "status", label: t("fields.status"), type: "select", required: true, options: STATUSES.map((v) => ({ value: v, label: t(`pointStatus.${v}`) })) },
    { key: "camera_id", label: t("fields.camera"), type: "select", options: (camerasQ.data ?? []).map((c) => ({ value: c.id, label: c.name })) },
    { key: "device_model", label: t("fields.model"), placeholder: "DS-K1T341CMF" },
    { key: "schedule", label: t("fields.schedule"), placeholder: "24/7" },
    { key: "methods", label: t("fields.methods"), type: "multiselect", options: METHODS.map((v) => ({ value: v, label: t(`method.${v}`) })) },
    { key: "anti_passback", label: t("fields.antiPassback"), type: "checkbox" },
  ];

  const submit = async (v: FormValues) => {
    const site = (sitesQ.data ?? []).find((s) => s.id === v.site_id);
    const body = { ...v, camera_id: v.camera_id || null, site_name: site?.name } as Partial<AcPoint>;
    try {
      if (form?.mode === "edit" && form.point) await update.mutateAsync({ id: form.point.id, body });
      else await create.mutateAsync(body);
      toast.success(t("common.saved"));
      setForm(null);
    } catch {
      toast.error(t("common.saveError"));
    }
  };

  const del = async (p: AcPoint) => {
    if (!window.confirm(t("common.confirmDelete", { interpolation: { escapeValue: false }, name: p.name }))) return;
    try {
      await remove.mutateAsync(p.id);
      setOpenId(null);
      toast.success(t("common.deleted"));
    } catch {
      toast.error(t("common.saveError"));
    }
  };

  return (
    <ConsolePage
      icon="ri-door-open-line"
      title={t("points.title")}
      subtitle={t("points.subtitle")}
      fill
      actions={<ToolButton icon="ri-add-line" variant="primary" onClick={() => setForm({ mode: "create" })}>{t("points.new")}</ToolButton>}
    >
      <div className="flex min-h-0 flex-1">
        <SiteTree className="hidden w-64 shrink-0 border-r md:flex" title={t("points.tree")} groups={groups} selectedGroup={site} selectedItem={openId} onSelectGroup={setSite} onSelectItem={setOpenId} />
        <div className="flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={search} onChange={setSearch} />
            <FilterSelect label={t("fields.type")} value={type} onChange={setType} options={[{ value: "", label: t("points.allTypes") }, ...TYPES.map((v) => ({ value: v, label: t(`pointType.${v}`) }))]} />
            <FilterSelect label={t("fields.status")} value={status} onChange={setStatus} options={[{ value: "", label: t("common.allStatuses") }, ...STATUSES.map((v) => ({ value: v, label: t(`pointStatus.${v}`) }))]} />
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-content-muted">{t("common.resultsN", { interpolation: { escapeValue: false }, count: rows.length })}</span>
              <SegmentedToggle label={t("common.viewMode")} value={view} onChange={setView} options={[{ value: "grid", label: <i className="ri-layout-grid-line" />, title: t("common.grid") }, { value: "table", label: <i className="ri-list-check" />, title: t("common.table") }]} />
            </div>
          </div>

          <LoadState loading={pointsQ.isLoading} error={pointsQ.error} empty={!rows.length} emptyText={t("points.empty")} onRetry={() => pointsQ.refetch()}>
            {view === "grid" ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                {rows.map((p) => {
                  const cam = p.camera_id ? camById.get(p.camera_id) : undefined;
                  return (
                    <button key={p.id} type="button" onClick={() => setOpenId(p.id)} className={`overflow-hidden rounded-xl border bg-card text-left transition hover:shadow-md ${openId === p.id ? "border-primary-500" : "border-line"}`}>
                      <div className="relative aspect-video bg-black">
                        {cam?.thumbnail_url && <img src={cam.thumbnail_url} alt="" loading="lazy" className={`h-full w-full object-cover ${cam.status === "offline" ? "opacity-40 grayscale" : ""}`} />}
                        <span className="absolute left-2 top-2"><StatusChip overlay tone={toneOf(POINT_TONE, p.status)} pulse={p.status === "alarm"}>{t(`pointStatus.${p.status}`)}</StatusChip></span>
                        <span className="absolute bottom-2 right-2 flex gap-1">
                          {(p.methods ?? []).map((m) => (
                            <span key={m} title={t(`method.${m}`)} className="flex h-6 w-6 items-center justify-center rounded bg-black/60 text-xs text-white"><i className={METHOD_ICON[m]} /></span>
                          ))}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 p-3">
                        <i className={`${POINT_ICON[p.type] ?? "ri-door-line"} mt-0.5 text-lg text-content-muted`} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-content">{p.name}</p>
                          <p className="truncate text-xs text-content-secondary">{p.site_name} · {p.zone || "—"}</p>
                          <p className="mt-1 text-[11px] text-content-muted">{t(`pointType.${p.type}`)} · {t(`direction.${p.direction ?? "bidirectional"}`)} · {t("points.lastEvent", { interpolation: { escapeValue: false }, when: relativeTime(p.last_event_at, i18n.language, now) })}</p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <Panel bodyClassName="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-subtle text-left text-[11px] uppercase tracking-wide text-content-muted">
                    <tr>
                      <th className="px-3 py-2">{t("fields.name")}</th>
                      <th className="px-3 py-2">{t("fields.site")}</th>
                      <th className="px-3 py-2">{t("fields.type")}</th>
                      <th className="px-3 py-2">{t("fields.methods")}</th>
                      <th className="px-3 py-2">{t("fields.model")}</th>
                      <th className="px-3 py-2">{t("fields.status")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {rows.map((p) => (
                      <tr key={p.id} onClick={() => setOpenId(p.id)} className="cursor-pointer hover:bg-subtle">
                        <td className="px-3 py-2 font-medium text-content">{p.name}</td>
                        <td className="px-3 py-2 text-content-secondary">{p.site_name} · {p.zone}</td>
                        <td className="px-3 py-2 text-content-secondary">{t(`pointType.${p.type}`)}</td>
                        <td className="px-3 py-2 text-content-secondary">{(p.methods ?? []).map((m) => <i key={m} className={`${METHOD_ICON[m]} mr-1`} title={t(`method.${m}`)} />)}</td>
                        <td className="px-3 py-2 font-mono text-xs text-content-secondary">{p.device_model}</td>
                        <td className="px-3 py-2"><StatusChip tone={toneOf(POINT_TONE, p.status)}>{t(`pointStatus.${p.status}`)}</StatusChip></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>
            )}
          </LoadState>
        </div>
      </div>

      <Drawer
        open={Boolean(current)}
        title={current?.name ?? ""}
        subtitle={current ? `${current.site_name ?? ""} · ${current.zone ?? ""}` : undefined}
        onClose={() => setOpenId(null)}
        footer={
          current && (
            <>
              <ToolButton icon="ri-delete-bin-line" variant="danger" onClick={() => del(current)}>{t("common.delete")}</ToolButton>
              <ToolButton icon="ri-edit-line" variant="primary" onClick={() => setForm({ mode: "edit", point: current })}>{t("common.edit")}</ToolButton>
            </>
          )
        }
      >
        {current && (
          <div className="space-y-3">
            {current.camera_id && camById.get(current.camera_id)?.snapshot_url && (
              <img src={camById.get(current.camera_id)?.snapshot_url ?? ""} alt="" className="aspect-video w-full rounded-lg object-cover" />
            )}
            <StatusChip tone={toneOf(POINT_TONE, current.status)}>{t(`pointStatus.${current.status}`)}</StatusChip>
            <div>
              <DetailRow label={t("fields.type")}>{t(`pointType.${current.type}`)}</DetailRow>
              <DetailRow label={t("fields.direction")}>{t(`direction.${current.direction ?? "bidirectional"}`)}</DetailRow>
              <DetailRow label={t("fields.methods")}>{(current.methods ?? []).map((m) => t(`method.${m}`)).join(", ")}</DetailRow>
              <DetailRow label={t("fields.model")}>{current.device_model ?? "—"}</DetailRow>
              <DetailRow label={t("fields.serial")}><span className="font-mono text-xs">{current.device_serial ?? "—"}</span></DetailRow>
              <DetailRow label={t("fields.camera")}>{current.camera_id ? camById.get(current.camera_id)?.name ?? "—" : "—"}</DetailRow>
              <DetailRow label={t("fields.schedule")}>{current.schedule ?? "—"}</DetailRow>
              <DetailRow label={t("fields.antiPassback")}>{current.anti_passback ? t("common.yes") : t("common.no")}</DetailRow>
              {current.notes && <DetailRow label={t("fields.notes")}>{current.notes}</DetailRow>}
            </div>
            <h3 className="pt-2 text-xs font-semibold uppercase tracking-wide text-content-muted">{t("points.recentEvents")}</h3>
            <LoadState loading={eventsQ.isLoading} error={eventsQ.error} empty={!eventsQ.data?.length} emptyText={t("events.empty")}>
              <ul className="-mx-3 divide-y divide-line">{(eventsQ.data ?? []).slice(0, 8).map((e) => <EventRow key={e.id} e={e} now={now} compact />)}</ul>
            </LoadState>
          </div>
        )}
      </Drawer>

      {form && (
        <FormModal
          title={form.mode === "edit" ? t("points.edit") : t("points.new")}
          fields={fields}
          initial={form.point ? { ...form.point, camera_id: form.point.camera_id ?? "", methods: form.point.methods ?? [] } as unknown as FormValues : { type: "door", direction: "bidirectional", status: "online", site_id: site ?? sitesQ.data?.[0]?.id ?? "", methods: ["card"] }}
          submitLabel={form.mode === "edit" ? t("common.save") : t("common.create")}
          busy={create.isPending || update.isPending}
          onSubmit={submit}
          onClose={() => setForm(null)}
        />
      )}
    </ConsolePage>
  );
}
